import { useRef, useState, useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Panel, DefaultButton, CommandBarButton, TooltipHost } from "@fluentui/react";
import readNDJSONStream from "ndjson-readablestream";

import appLogo from "/dyn_logo-primary.png";
import styles from "./Chat.module.css";

import {
    chatApi,
    configApi,
    uploadFileApi,
    RetrievalMode,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    ResponseMessage,
    SpeechConfig,
    FoundryAgentInfo
} from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { HistoryPanel } from "../../components/HistoryPanel";
import { HistoryProviderOptions, useHistoryManager } from "../../components/HistoryProviders";
import { SettingsButton } from "../../components/SettingsButton";
import { UploadFile } from "../../components/UploadFile";
import { SquareRegular, History24Regular, Compose24Regular } from "@fluentui/react-icons";
import { Button } from "@fluentui/react-components";
import { useLogin, getToken, requireAccessControl } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { TokenClaimsDisplay } from "../../components/TokenClaimsDisplay";
import { LoginContext } from "../../loginContext";
import { LanguagePicker } from "../../i18n/LanguagePicker";
import { Settings } from "../../components/Settings/Settings";

const Chat = () => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.3);
    const [seed, setSeed] = useState<number | null>(null);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(1.9);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [retrieveCount, setRetrieveCount] = useState<number>(8);
    const [agenticReasoningEffort, setRetrievalReasoningEffort] = useState<string>("minimal");
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [useQueryRewriting, setUseQueryRewriting] = useState<boolean>(false);
    const [reasoningEffort, setReasoningEffort] = useState<string>("");
    const [streamingEnabled, setStreamingEnabled] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const previousShouldStreamRef = useRef<boolean>(true);
    const forcedStreamingRef = useRef<boolean>(false);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [includeCategory, setIncludeCategory] = useState<string>("");
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(false);
    const [searchTextEmbeddings, setSearchTextEmbeddings] = useState<boolean>(true);
    const [searchImageEmbeddings, setSearchImageEmbeddings] = useState<boolean>(false);
    const [sendTextSources, setSendTextSources] = useState<boolean>(true);
    const [sendImageSources, setSendImageSources] = useState<boolean>(false);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    // Progress message shown in the loading row while the backend works through the
    // pipeline (e.g. "Querying agent 'web-search'…"). Cleared when content streams.
    const [loadingStatus, setLoadingStatus] = useState<string>("");
    // Min-display tracking: enforce that any status stays visible for at least 2s before
    // being replaced, so quick stages don't flash. When a new status arrives during that
    // window, we queue it and apply it after the current one's minimum has elapsed.
    const lastStatusUpdateRef = useRef<number>(0);
    const pendingStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [restoredQuestion, setRestoredQuestion] = useState<string>("");
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [speechUrls, setSpeechUrls] = useState<(string | null)[]>([]);
    const [feedbackState, setFeedbackState] = useState<Record<number, "positive" | "negative" | "neutral">>({});

    const [showMultimodalOptions, setShowMultimodalOptions] = useState<boolean>(false);
    const [showSemanticRankerOption, setShowSemanticRankerOption] = useState<boolean>(false);
    const [showQueryRewritingOption, setShowQueryRewritingOption] = useState<boolean>(false);
    const [showReasoningEffortOption, setShowReasoningEffortOption] = useState<boolean>(false);
    const [showVectorOption, setShowVectorOption] = useState<boolean>(false);
    const [showUserUpload, setShowUserUpload] = useState<boolean>(false);
    const [maxUploadSizeMb, setMaxUploadSizeMb] = useState<number>(10);
    const [showLanguagePicker, setshowLanguagePicker] = useState<boolean>(false);
    const [showSpeechOutputBrowser, setShowSpeechOutputBrowser] = useState<boolean>(false);
    const [showSpeechOutputAzure, setShowSpeechOutputAzure] = useState<boolean>(false);
    const [showChatHistoryBrowser, setShowChatHistoryBrowser] = useState<boolean>(false);
    const [showChatHistoryCosmos, setShowChatHistoryCosmos] = useState<boolean>(false);
    const [showAgenticRetrievalOption, setShowAgenticRetrievalOption] = useState<boolean>(false);
    const [webSourceSupported, setWebSourceSupported] = useState<boolean>(false);
    const [webSourceEnabled, setWebSourceEnabled] = useState<boolean>(false);
    const [sharePointSourceSupported, setSharePointSourceSupported] = useState<boolean>(false);
    const [sharePointSourceEnabled, setSharePointSourceEnabled] = useState<boolean>(false);
    const [useAgenticKnowledgeBase, setUseAgenticRetrieval] = useState<boolean>(false);
    const [hideMinimalRetrievalReasoningOption, setHideMinimalRetrievalReasoningOption] = useState<boolean>(false);
    const [foundryAgents, setFoundryAgents] = useState<FoundryAgentInfo[]>([]);
    const [showDevTools, setShowDevTools] = useState<boolean>(false);
    const streamingDisabledByOverrides = useAgenticKnowledgeBase && webSourceEnabled;

    const conversationUploadInputRef = useRef<HTMLInputElement>(null);
    const [conversationUploadMessage, setConversationUploadMessage] = useState<string | null>(null);
    const [isConversationUploading, setIsConversationUploading] = useState(false);
    // Pre-assigned session ID generated at mount. Used as session_state on the first chat
    // message so files uploaded before any conversation starts are immediately retrievable.
    const preSessionIdRef = useRef<string>(crypto.randomUUID());

    const audio = useRef(new Audio()).current;
    const [isPlaying, setIsPlaying] = useState(false);

    const speechConfig: SpeechConfig = {
        speechUrls,
        setSpeechUrls,
        audio,
        isPlaying,
        setIsPlaying
    };

    const getConfig = async () => {
        configApi().then(config => {
            setShowMultimodalOptions(config.showMultimodalOptions);
            if (config.showMultimodalOptions) {
                // Initialize from server config so defaults match deployment settings
                setSendTextSources(config.ragSendTextSources !== undefined ? config.ragSendTextSources : true);
                setSendImageSources(config.ragSendImageSources);
                setSearchTextEmbeddings(config.ragSearchTextEmbeddings);
                setSearchImageEmbeddings(config.ragSearchImageEmbeddings);
            }
            setUseSemanticRanker(config.showSemanticRankerOption);
            setShowSemanticRankerOption(config.showSemanticRankerOption);
            setUseQueryRewriting(config.showQueryRewritingOption);
            setShowQueryRewritingOption(config.showQueryRewritingOption);
            setShowReasoningEffortOption(config.showReasoningEffortOption);
            setStreamingEnabled(config.streamingEnabled);
            if (config.showReasoningEffortOption) {
                setReasoningEffort(config.defaultReasoningEffort);
            }
            setShowVectorOption(config.showVectorOption);
            if (!config.showVectorOption) {
                setRetrievalMode(RetrievalMode.Text);
            }
            setShowUserUpload(config.showUserUpload);
            setMaxUploadSizeMb(config.maxUploadSizeMb);
            setshowLanguagePicker(config.showLanguagePicker);
            setShowSpeechOutputBrowser(config.showSpeechOutputBrowser);
            setShowSpeechOutputAzure(config.showSpeechOutputAzure);
            setShowChatHistoryBrowser(config.showChatHistoryBrowser);
            setShowChatHistoryCosmos(config.showChatHistoryCosmos);
            setShowAgenticRetrievalOption(config.showAgenticRetrievalOption);
            setUseAgenticRetrieval(config.showAgenticRetrievalOption);
            setWebSourceSupported(config.webSourceEnabled);
            setWebSourceEnabled(config.webSourceEnabled);
            setSharePointSourceSupported(config.sharepointSourceEnabled);
            setSharePointSourceEnabled(config.sharepointSourceEnabled);
            setFoundryAgents(config.foundryAgents ?? []);
            setShowDevTools(config.showDevTools);
            if (config.showAgenticRetrievalOption) {
                setRetrieveCount(10);
            }
            const defaultRetrievalEffort = config.defaultRetrievalReasoningEffort ?? "minimal";
            setHideMinimalRetrievalReasoningOption(config.webSourceEnabled);
            setRetrievalReasoningEffort(defaultRetrievalEffort);
        });
    };

    /** Apply a new loading status, but never replace the current message faster than
     *  every 2 seconds — gives the user time to actually read each step. If a new
     *  status arrives during the cooldown window, we queue it for delayed display
     *  (and intermediate updates between get coalesced — the latest wins). */
    const setLoadingStatusWithMinDuration = (status: string) => {
        const minDisplayMs = 2000;
        const now = Date.now();
        const elapsed = now - lastStatusUpdateRef.current;
        if (pendingStatusTimeoutRef.current) {
            clearTimeout(pendingStatusTimeoutRef.current);
            pendingStatusTimeoutRef.current = null;
        }
        if (lastStatusUpdateRef.current === 0 || elapsed >= minDisplayMs) {
            setLoadingStatus(status);
            lastStatusUpdateRef.current = Date.now();
        } else {
            const delay = minDisplayMs - elapsed;
            pendingStatusTimeoutRef.current = setTimeout(() => {
                setLoadingStatus(status);
                lastStatusUpdateRef.current = Date.now();
                pendingStatusTimeoutRef.current = null;
            }, delay);
        }
    };

    /** Clear status state and any pending timeout — called at request start and after
     *  the loading row unmounts (when content arrives). */
    const resetLoadingStatus = () => {
        if (pendingStatusTimeoutRef.current) {
            clearTimeout(pendingStatusTimeoutRef.current);
            pendingStatusTimeoutRef.current = null;
        }
        setLoadingStatus("");
        lastStatusUpdateRef.current = 0;
    };

    const handleAsyncRequest = async (question: string, answers: [string, ChatAppResponse][], responseBody: ReadableStream<any>, signal: AbortSignal) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {
            message: { content: "", role: "assistant" },
            delta: { content: "", role: "assistant" },
            context: { data_points: { text: [], images: [], citations: [] }, thoughts: [], followup_questions: null },
            session_state: null
        };

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    const latestResponse: ChatAppResponse = {
                        ...askResponse,
                        message: { content: answer, role: askResponse.message.role }
                    };
                    setStreamedAnswers([...answers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (signal.aborted) {
                    break;
                }
                if (event["context"] && event["context"]["data_points"]) {
                    event["message"] = event["delta"];
                    askResponse = event as ChatAppResponse;
                } else if (event["delta"] && event["delta"]["content"]) {
                    setIsLoading(false);
                    resetLoadingStatus();
                    await updateState(event["delta"]["content"]);
                } else if (event["status"]) {
                    // Backend progress message ("Thinking…", "Querying agent 'X'…", etc.)
                    setLoadingStatusWithMinDuration(String(event["status"]));
                } else if (event["context"]) {
                    // Update context with new keys from latest event
                    askResponse.context = { ...askResponse.context, ...event["context"] };
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // User clicked stop - don't treat as error
                console.log("Stream aborted by user");
            } else {
                throw e; // Re-throw other errors to be caught by makeApiRequest
            }
        } finally {
            setIsStreaming(false);
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            message: { content: answer, role: askResponse.message.role }
        };
        return fullResponse;
    };

    const client = useLogin ? useMsal().instance : undefined;
    const { loggedIn } = useContext(LoginContext);

    const historyProvider: HistoryProviderOptions = (() => {
        if (useLogin && showChatHistoryCosmos) return HistoryProviderOptions.CosmosDB;
        if (showChatHistoryBrowser) return HistoryProviderOptions.IndexedDB;
        return HistoryProviderOptions.None;
    })();
    const historyManager = useHistoryManager(historyProvider);

    const updateStreamingPreference = (isStreamingEnabledOverride: boolean, disablesStreamingOverride: boolean) => {
        if (!isStreamingEnabledOverride) {
            setShouldStream(current => {
                if (!forcedStreamingRef.current) {
                    previousShouldStreamRef.current = current;
                }
                forcedStreamingRef.current = true;
                return current ? false : current;
            });
            return;
        }

        if (disablesStreamingOverride) {
            setShouldStream(current => {
                if (!forcedStreamingRef.current) {
                    previousShouldStreamRef.current = current;
                }
                forcedStreamingRef.current = true;
                return current ? false : current;
            });
            return;
        }

        forcedStreamingRef.current = false;
        setShouldStream(current => {
            const desiredShouldStream = previousShouldStreamRef.current;
            return current === desiredShouldStream ? current : desiredShouldStream;
        });
    };

    const makeApiRequest = async (question: string) => {
        const controller = new AbortController();
        setAbortController(controller);
        lastQuestionRef.current = question;

        error && setError(undefined);
        setRestoredQuestion("");
        setIsLoading(true);
        resetLoadingStatus();
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        // Seed the streamed view with the already-committed answers so prior
        // turns stay visible through the loading window — and any stale streamed
        // state from a previous turn is overwritten rather than ghosting through.
        setStreamedAnswers(answers);

        const token = client ? await getToken(client) : undefined;

        try {
            const messages: ResponseMessage[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].message.content, role: "assistant" }
            ]);

            const request: ChatAppRequest = {
                messages: [...messages, { content: question, role: "user" }],
                context: {
                    overrides: {
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        include_category: includeCategory.length === 0 ? undefined : includeCategory,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        top: retrieveCount,
                        ...(useAgenticKnowledgeBase ? { retrieval_reasoning_effort: agenticReasoningEffort } : {}),
                        temperature: temperature,
                        minimum_reranker_score: minimumRerankerScore,
                        minimum_search_score: minimumSearchScore,
                        retrieval_mode: retrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        query_rewriting: useQueryRewriting,
                        reasoning_effort: reasoningEffort,
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        search_text_embeddings: searchTextEmbeddings,
                        search_image_embeddings: searchImageEmbeddings,
                        send_text_sources: sendTextSources,
                        send_image_sources: sendImageSources,
                        language: i18n.language,
                        use_agentic_knowledgebase: useAgenticKnowledgeBase,
                        use_web_source: webSourceSupported ? webSourceEnabled : false,
                        use_sharepoint_source: sharePointSourceSupported ? sharePointSourceEnabled : false,
                        ...(seed !== null ? { seed: seed } : {})
                    }
                },
                // AI Chat Protocol: Client must pass on any session state received from the server.
                // Fall back to preSessionIdRef so files uploaded before the first message are retrievable.
                session_state: answers.length ? answers[answers.length - 1][1].session_state : preSessionIdRef.current
            };

            const response = await chatApi(request, shouldStream, token, controller.signal);
            if (!response.body) {
                throw Error("No response body");
            }
            if (response.status > 299 || !response.ok) {
                throw Error(`Request failed with status ${response.status}`);
            }
            if (shouldStream) {
                const parsedResponse: ChatAppResponse = await handleAsyncRequest(question, answers, response.body, controller.signal);
                // Only add to answers if we got content, otherwise restore question to input
                if (parsedResponse.message.content) {
                    setAnswers([...answers, [question, parsedResponse]]);
                    if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                        const token = client ? await getToken(client) : undefined;
                        historyManager.addItem(parsedResponse.session_state, [...answers, [question, parsedResponse]], token);
                    }
                } else {
                    // Stopped before any content arrived - restore question to input
                    lastQuestionRef.current = answers.length > 0 ? answers[answers.length - 1][0] : "";
                    setRestoredQuestion(question);
                }
            } else {
                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (parsedResponse.error) {
                    throw Error(parsedResponse.error);
                }
                setAnswers([...answers, [question, parsedResponse as ChatAppResponse]]);
                if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                    const token = client ? await getToken(client) : undefined;
                    historyManager.addItem(parsedResponse.session_state, [...answers, [question, parsedResponse as ChatAppResponse]], token);
                }
            }
            setSpeechUrls([...speechUrls, null]);
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Stopped during loading - restore question to input
                lastQuestionRef.current = answers.length > 0 ? answers[answers.length - 1][0] : "";
                setRestoredQuestion(question);
            } else {
                setError(e);
            }
        } finally {
            setIsLoading(false);
            setAbortController(null);
        }
    };

    const handleFeedback = (index: number, feedback: "positive" | "negative" | "neutral") => {
        setFeedbackState(prev => ({ ...prev, [index]: feedback }));
    };

    const newChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        preSessionIdRef.current = crypto.randomUUID();
        setSpeechUrls([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
        setRestoredQuestion("");
        setConversationUploadMessage(null);
        setFeedbackState({});
    };

    const clearChat = () => {
        newChat();
    };

    const currentConversationId =
        answers.length > 0 && typeof answers[answers.length - 1][1].session_state === "string"
            ? (answers[answers.length - 1][1].session_state as string)
            : null;

    const onAddFileToConversation = () => {
        setConversationUploadMessage(null);
        conversationUploadInputRef.current?.click();
    };

    const onConversationFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !client) return;
        e.target.value = "";
        setIsConversationUploading(true);
        setConversationUploadMessage(null);
        try {
            const token = await getToken(client);
            if (!token) {
                setConversationUploadMessage(t("upload.uploadedFileError"));
                return;
            }
            const formData = new FormData();
            formData.append("file", file);
            // Use the pre-assigned session ID when no conversation exists yet,
            // so the file is immediately retrievable on the first chat message.
            const conversationId = currentConversationId ?? preSessionIdRef.current;
            await uploadFileApi(formData, token, conversationId);
            setConversationUploadMessage(t("upload.fileAddedToConversation"));
            setTimeout(() => setConversationUploadMessage(null), 4000);
        } catch (err) {
            setConversationUploadMessage(t("upload.uploadedFileError"));
        } finally {
            setIsConversationUploading(false);
        }
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    }, [streamedAnswers]);
    useEffect(() => {
        getConfig();
    }, []);

    // Preserve streaming preference when agentic retrieval forces streaming off.
    useEffect(() => {
        updateStreamingPreference(streamingEnabled, streamingDisabledByOverrides);
    }, [streamingDisabledByOverrides, streamingEnabled]);

    const handleSettingsChange = (field: string, value: any) => {
        switch (field) {
            case "promptTemplate":
                setPromptTemplate(value);
                break;
            case "temperature":
                setTemperature(value);
                break;
            case "seed":
                setSeed(value);
                break;
            case "minimumRerankerScore":
                setMinimumRerankerScore(value);
                break;
            case "minimumSearchScore":
                setMinimumSearchScore(value);
                break;
            case "retrieveCount":
                setRetrieveCount(value);
                break;
            case "agenticReasoningEffort": {
                setRetrievalReasoningEffort(value);
                // If selecting minimal while web source is enabled, disable web source
                if (value === "minimal" && webSourceEnabled) {
                    setWebSourceEnabled(false);
                    setHideMinimalRetrievalReasoningOption(false);
                    // Web source was disabled, so restore streaming
                    updateStreamingPreference(streamingEnabled, false);
                }
                break;
            }
            case "useSemanticRanker":
                setUseSemanticRanker(value);
                break;
            case "useQueryRewriting":
                setUseQueryRewriting(value);
                break;
            case "reasoningEffort":
                setReasoningEffort(value);
                break;
            case "useSemanticCaptions":
                setUseSemanticCaptions(value);
                break;
            case "excludeCategory":
                setExcludeCategory(value);
                break;
            case "includeCategory":
                setIncludeCategory(value);
                break;
            case "shouldStream":
                {
                    const normalizedShouldStream = !!value;
                    forcedStreamingRef.current = false;
                    previousShouldStreamRef.current = normalizedShouldStream;
                    setShouldStream(normalizedShouldStream);
                }
                break;
            case "useSuggestFollowupQuestions":
                setUseSuggestFollowupQuestions(value);
                break;
            case "llmInputs":
                break;
            case "sendTextSources":
                setSendTextSources(value);
                break;
            case "sendImageSources":
                setSendImageSources(value);
                break;
            case "searchTextEmbeddings":
                setSearchTextEmbeddings(value);
                break;
            case "searchImageEmbeddings":
                setSearchImageEmbeddings(value);
                break;
            case "retrievalMode":
                setRetrievalMode(value);
                break;
            case "useAgenticKnowledgeBase": {
                setUseAgenticRetrieval(value);
                let effectiveWebSource = webSourceEnabled;
                if (!value && webSourceEnabled) {
                    effectiveWebSource = false;
                    setWebSourceEnabled(false);
                    setHideMinimalRetrievalReasoningOption(false);
                }
                // Only web source disables streaming
                const shouldDisableStreaming = !!value && effectiveWebSource;
                updateStreamingPreference(streamingEnabled, shouldDisableStreaming);
                break;
            }
            case "useWebSource":
                if (!webSourceSupported) {
                    setWebSourceEnabled(false);
                    return;
                }
                const normalizedWebSource = !!value;
                setWebSourceEnabled(normalizedWebSource);
                setHideMinimalRetrievalReasoningOption(normalizedWebSource);
                // When enabling web source, disable follow-up questions and streaming
                if (normalizedWebSource) {
                    setUseSuggestFollowupQuestions(false);
                }
                const shouldDisableStreaming = useAgenticKnowledgeBase && normalizedWebSource;
                updateStreamingPreference(streamingEnabled, shouldDisableStreaming);
                break;
            case "useSharePointSource":
                if (!sharePointSourceSupported) {
                    setSharePointSourceEnabled(false);
                    return;
                }
                setSharePointSourceEnabled(!!value);
                break;
        }
    };

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const onStopClick = async () => {
        try {
            if (abortController) {
                abortController.abort();
            }
        } catch (e) {
            console.log("An error occurred trying to stop the stream: ", e);
        }
    };

    const { t, i18n } = useTranslation();

    // Friendly typewriter-style placeholder for the question input: types each phrase
    // out, pauses, deletes it, and moves to a different randomly-chosen phrase. All
    // intervals are jittered so it feels human rather than mechanical.
    //
    // Phrase source depends on conversation state:
    //  - Empty conversation → cycle the welcoming greetings.
    //  - Conversation in progress → use the latest answer's follow-up suggestions
    //    (when the model produced any). The cycling placeholder doubles as a hint
    //    for what to ask next.
    //  - Mid-request or no follow-ups available → empty list, placeholder clears.
    const placeholderPhrases = useMemo<string[]>(() => {
        if (answers.length > 0) {
            const lastAnswer = answers[answers.length - 1][1];
            const followups = lastAnswer.context?.followup_questions;
            if (Array.isArray(followups) && followups.length > 0) {
                return followups;
            }
            return [];
        }
        if (isStreaming || isLoading) {
            return [];
        }
        const list = t("defaultExamples.placeholders", { returnObjects: true });
        if (Array.isArray(list) && list.length > 0) return list as string[];
        return [t("defaultExamples.placeholder")];
    }, [t, answers, isStreaming, isLoading]);

    const [placeholderText, setPlaceholderText] = useState<string>("");

    useEffect(() => {
        // Reset immediately so a stale phrase from the previous source doesn't
        // linger before the new typing cycle starts.
        setPlaceholderText("");
        if (placeholderPhrases.length === 0) return;

        // ~120 wpm mental-model base, with two stacked sources of randomness:
        //  1. Per-char jitter ±15ms — finger speed isn't perfectly constant.
        //  2. Per-word speed factor — some words you breeze through, others you have
        //     to focus on. Picked once when a new word starts and reused for that word.
        const TYPING_BASE_MS = 20;
        const TYPING_JITTER_MS = 12;

        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        let phraseIdx = Math.floor(Math.random() * placeholderPhrases.length);
        let charIdx = 0;
        let typoBuf = "";
        let overshootTarget = 0;
        let phase: "typing" | "typoOvershoot" | "typoBackspace" | "pausing" | "deleting" = "typing";

        const rand = (min: number, max: number) => min + Math.random() * (max - min);
        const randLetter = () => "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)];
        // 0.7 → "easy word, fingers fly", 1.4 → "this one needs a little focus"
        const pickWordFactor = () => rand(0.7, 1.4);
        let wordFactor = pickWordFactor();

        const typeDelay = () => {
            const jitter = (Math.random() * 2 - 1) * TYPING_JITTER_MS;
            return Math.max(8, (TYPING_BASE_MS + jitter) * wordFactor);
        };

        const step = () => {
            if (cancelled) return;
            const phrase = placeholderPhrases[phraseIdx];

            if (phase === "typing") {
                if (charIdx > 3 && charIdx < phrase.length - 4 && Math.random() < 0.005) {
                    let wrong = randLetter();
                    const target = phrase[charIdx]?.toLowerCase();
                    if (target && /[a-z]/.test(target)) {
                        while (wrong === target) wrong = randLetter();
                    }
                    typoBuf = wrong;
                    overshootTarget = Math.random() < 0.5 ? 2 : 3;
                    phase = "typoOvershoot";
                    setPlaceholderText(phrase.slice(0, charIdx) + typoBuf);
                    timeoutId = setTimeout(step, typeDelay());
                    return;
                }
                charIdx++;
                setPlaceholderText(phrase.slice(0, charIdx));
                if (charIdx >= phrase.length) {
                    // Sit on the finished phrase for ~15 seconds before clearing and
                    // moving on. Most users hit the input quickly; this is here for
                    // those who linger and watch.
                    phase = "pausing";
                    timeoutId = setTimeout(step, rand(14000, 16000));
                } else {
                    const justTyped = phrase[charIdx - 1];
                    if (justTyped === " ") {
                        wordFactor = pickWordFactor();
                    }
                    // Slight pause after a word (~4% chance after a space) — a beat
                    // around half a second. Plus rare general hesitations.
                    const wordPause = justTyped === " " && Math.random() < 0.04;
                    const hesitate = !wordPause && Math.random() < 0.01;
                    const delay = wordPause ? rand(380, 650) : hesitate ? rand(130, 220) : typeDelay();
                    timeoutId = setTimeout(step, delay);
                }
            } else if (phase === "typoOvershoot") {
                const overshootCount = typoBuf.length - 1;
                if (overshootCount < overshootTarget) {
                    const nextChar = phrase[charIdx + typoBuf.length];
                    if (nextChar === undefined) {
                        phase = "typoBackspace";
                        timeoutId = setTimeout(step, rand(200, 380));
                        return;
                    }
                    typoBuf += nextChar;
                    setPlaceholderText(phrase.slice(0, charIdx) + typoBuf);
                    timeoutId = setTimeout(step, typeDelay());
                } else {
                    // Two-beat recognition: "oh, that's wrong" + "...recalculating"
                    phase = "typoBackspace";
                    timeoutId = setTimeout(step, rand(380, 720));
                }
            } else if (phase === "typoBackspace") {
                typoBuf = typoBuf.slice(0, -1);
                setPlaceholderText(phrase.slice(0, charIdx) + typoBuf);
                if (typoBuf.length === 0) {
                    phase = "typing";
                    timeoutId = setTimeout(step, rand(80, 170));
                } else {
                    timeoutId = setTimeout(step, rand(18, 42));
                }
            } else if (phase === "pausing") {
                phase = "deleting";
                timeoutId = setTimeout(step, 0);
            } else {
                charIdx--;
                setPlaceholderText(phrase.slice(0, charIdx));
                if (charIdx <= 0) {
                    if (placeholderPhrases.length > 1) {
                        let next = phraseIdx;
                        while (next === phraseIdx) next = Math.floor(Math.random() * placeholderPhrases.length);
                        phraseIdx = next;
                    }
                    phase = "typing";
                    wordFactor = pickWordFactor();
                    timeoutId = setTimeout(step, rand(300, 600));
                } else {
                    timeoutId = setTimeout(step, rand(15, 35));
                }
            }
        };

        timeoutId = setTimeout(step, rand(200, 500));

        return () => {
            cancelled = true;
            if (timeoutId !== undefined) clearTimeout(timeoutId);
        };
    }, [placeholderPhrases]);

    return (
        <div className={styles.container}>
            {/* Setting the page title using react-helmet-async */}
            <Helmet>
                <title>{t("pageTitle")}</title>
            </Helmet>
            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {/* Top-left icon menu — history lives here so the panel slides from the
                        left and doesn't fight with the right-side AnalysisPanel (citations,
                        supporting content). */}
                    <div className={styles.chatActionsLeft}>
                        {((useLogin && showChatHistoryCosmos) || showChatHistoryBrowser) && (
                            <TooltipHost content={t("history.openChatHistory")} calloutProps={{ gapSpace: 4 }}>
                                <Button
                                    appearance="subtle"
                                    icon={<History24Regular />}
                                    onClick={() => setIsHistoryPanelOpen(!isHistoryPanelOpen)}
                                    aria-label={t("history.openChatHistory")}
                                />
                            </TooltipHost>
                        )}
                    </div>
                    {/* Top-right icon menu — settings/dev tools */}
                    <div className={styles.chatActions}>
                        {showDevTools && <SettingsButton className={styles.commandButton} onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)} />}
                    </div>
                    {!lastQuestionRef.current ? (
                        <div className={styles.chatEmptyState}>
                            <img src={appLogo} alt="Dynasty Financial Partners" height="72" className={styles.chatEmptyStateLogo} />

                            <h1 className={styles.chatEmptyStateTitle}>{t("chatEmptyStateTitle")}</h1>
                            <p className={styles.chatEmptyStateSubtitle}>
                                {t("chatEmptyStateSubtitle")}
                                <a href="/assets/ai-chat_disclaimer-fed873b5.pdf" target="_blank" rel="noopener noreferrer">
                                    {t("chatEmptyStateDisclaimerLink")}
                                </a>
                            </p>

                            <div className={styles.chatEmptyStateUseCases}>
                                <p className={styles.chatEmptyStateUseCasesTitle}>{t("chatEmptyStateUseCasesTitle")}</p>
                                <ul className={styles.chatEmptyStateUseCasesList}>
                                    {(t("chatEmptyStateUseCases", { returnObjects: true }) as string[]).map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </div>

                            <p className={styles.chatEmptyStateNote}>{t("chatEmptyStateNote")}</p>

                            {showLanguagePicker && <LanguagePicker onLanguageChange={newLang => i18n.changeLanguage(newLang)} />}

                            {/* <ExampleList onExampleClicked={onExampleClicked} useMultimodalAnswering={showMultimodalOptions} foundryAgents={foundryAgents} /> */}
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {isStreaming &&
                                streamedAnswers.map((streamedAnswer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={streamedAnswer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={true}
                                                key={index}
                                                answer={streamedAnswer[1]}
                                                index={index}
                                                speechConfig={speechConfig}
                                                isSelected={false}
                                                onCitationClicked={c => onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                showSpeechOutputAzure={showSpeechOutputAzure}
                                                showSpeechOutputBrowser={showSpeechOutputBrowser}
                                                showDevTools={showDevTools}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {!isStreaming &&
                                answers.map((answer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={answer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={false}
                                                key={index}
                                                answer={answer[1]}
                                                index={index}
                                                speechConfig={speechConfig}
                                                isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                onCitationClicked={c => onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                                showSpeechOutputAzure={showSpeechOutputAzure}
                                                showSpeechOutputBrowser={showSpeechOutputBrowser}
                                                showDevTools={showDevTools}
                                                onFeedback={handleFeedback}
                                                feedback={feedbackState[index]}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {isLoading && (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading status={loadingStatus} />
                                    </div>
                                </>
                            )}
                            {error ? (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}

                    <div className={styles.chatInput}>
                        {(isStreaming || isLoading) && (
                            <div
                                className={styles.stopGeneratingContainer}
                                role="button"
                                aria-label={t("tooltips.stopStreaming")}
                                tabIndex={0}
                                onClick={onStopClick}
                                onKeyDown={e => (e.key === "Enter" || e.key === " " ? onStopClick() : null)}
                            >
                                <SquareRegular className={styles.stopGeneratingIcon} aria-hidden="true" />
                                <span className={styles.stopGeneratingText} aria-hidden="true">
                                    {t("tooltips.stopStreaming")}
                                </span>
                            </div>
                        )}
                        <div className={styles.chatInputRow}>
                            <div className={styles.chatInputButtons}>
                                <TooltipHost content={t("clearChat")} calloutProps={{ gapSpace: 4 }}>
                                    <CommandBarButton
                                        role="button"
                                        styles={{
                                            icon: { color: "#FFFFFF" },
                                            iconDisabled: { color: "#BDBDBD !important" },
                                            root: {
                                                color: "#FFFFFF",
                                                background: "radial-gradient(109.81% 107.82% at 100.1% 90.19%, #0F6CBD 33.63%, #2D87C3 70.31%, #8DDDD8 100%)"
                                            },
                                            rootDisabled: { background: "#F0F0F0" }
                                        }}
                                        className={styles.chatInputButton}
                                        onRenderIcon={() => <Compose24Regular style={{ color: "#FFFFFF" }} />}
                                        onClick={clearChat}
                                        disabled={answers.length === 0 || isLoading}
                                        aria-label={t("clearChat")}
                                    />
                                </TooltipHost>
                            </div>
                            <QuestionInput
                                clearOnSend
                                placeholder={placeholderText}
                                disabled={isLoading}
                                onSend={question => makeApiRequest(question)}
                                initQuestion={restoredQuestion}
                                attachmentSlot={
                                    showUserUpload ? (
                                        <UploadFile
                                            disabled={!loggedIn}
                                            maxSizeMb={maxUploadSizeMb}
                                            conversationId={currentConversationId ?? preSessionIdRef.current}
                                        />
                                    ) : undefined
                                }
                            />
                        </div>
                    </div>
                </div>

                {(() => {
                    // While streaming, the in-progress turn lives in streamedAnswers but isn't yet
                    // committed to answers — citations can be clicked mid-stream, so look up the
                    // selected turn in whichever array currently holds it.
                    const sourceAnswers = isStreaming ? streamedAnswers : answers;
                    const selectedTurn = selectedAnswer >= 0 && selectedAnswer < sourceAnswers.length ? sourceAnswers[selectedAnswer] : undefined;
                    if (!selectedTurn || !activeAnalysisPanelTab) return null;
                    return (
                        <AnalysisPanel
                            className={styles.chatAnalysisPanel}
                            activeCitation={activeCitation}
                            onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                            onClose={() => {
                                setActiveAnalysisPanelTab(undefined);
                                setActiveCitation(undefined);
                            }}
                            citationHeight="810px"
                            answer={selectedTurn[1]}
                            activeTab={activeAnalysisPanelTab}
                            onCitationClicked={c => onShowCitation(c, selectedAnswer)}
                            showDevTools={showDevTools}
                        />
                    );
                })()}

                {((useLogin && showChatHistoryCosmos) || showChatHistoryBrowser) && (
                    <HistoryPanel
                        provider={historyProvider}
                        isOpen={isHistoryPanelOpen}
                        notify={!isStreaming && !isLoading}
                        onToggle={() => setIsHistoryPanelOpen(!isHistoryPanelOpen)}
                        activeSessionId={currentConversationId}
                        // showUpload={false}
                        uploadDisabled={!loggedIn}
                        maxUploadSizeMb={maxUploadSizeMb}
                        onChatSelected={answers => {
                            if (answers.length === 0) return;
                            setAnswers(answers);
                            lastQuestionRef.current = answers[answers.length - 1][0];
                        }}
                    />
                )}

                <Panel
                    headerText={t("labels.headerText")}
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel={t("labels.closeButton")}
                    onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>{t("labels.closeButton")}</DefaultButton>}
                    isFooterAtBottom={true}
                >
                    <Settings
                        promptTemplate={promptTemplate}
                        temperature={temperature}
                        retrieveCount={retrieveCount}
                        agenticReasoningEffort={agenticReasoningEffort}
                        seed={seed}
                        minimumSearchScore={minimumSearchScore}
                        minimumRerankerScore={minimumRerankerScore}
                        useSemanticRanker={useSemanticRanker}
                        useSemanticCaptions={useSemanticCaptions}
                        useQueryRewriting={useQueryRewriting}
                        reasoningEffort={reasoningEffort}
                        excludeCategory={excludeCategory}
                        includeCategory={includeCategory}
                        retrievalMode={retrievalMode}
                        showMultimodalOptions={showMultimodalOptions}
                        sendTextSources={sendTextSources}
                        sendImageSources={sendImageSources}
                        searchTextEmbeddings={searchTextEmbeddings}
                        searchImageEmbeddings={searchImageEmbeddings}
                        showSemanticRankerOption={showSemanticRankerOption}
                        showQueryRewritingOption={showQueryRewritingOption}
                        showReasoningEffortOption={showReasoningEffortOption}
                        showVectorOption={showVectorOption}
                        useLogin={!!useLogin}
                        loggedIn={loggedIn}
                        requireAccessControl={requireAccessControl}
                        shouldStream={shouldStream}
                        streamingEnabled={streamingEnabled}
                        useSuggestFollowupQuestions={useSuggestFollowupQuestions}
                        showAgenticRetrievalOption={showAgenticRetrievalOption}
                        useAgenticKnowledgeBase={useAgenticKnowledgeBase}
                        useWebSource={webSourceEnabled}
                        showWebSourceOption={webSourceSupported}
                        useSharePointSource={sharePointSourceEnabled}
                        showSharePointSourceOption={sharePointSourceSupported}
                        hideMinimalRetrievalReasoningOption={hideMinimalRetrievalReasoningOption}
                        onChange={handleSettingsChange}
                    />
                    {useLogin && <TokenClaimsDisplay />}
                </Panel>
            </div>
        </div>
    );
};

export default Chat;
